import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TradeResultCard from '../../Components/PerpsComponents/TradeResultCard/TradeResultCard';
import { PurpleButton } from '../../Components/Buttons/PurpleButton';
import { decodeTradeData, extractEncodedFromUrl } from '../../utils/shareableTrade';

/**
 * Share Trade Page
 * Displays a trade result card from a shareable URL
 * Can be used for referrals and sharing trade results
 */
const ShareTradePage = () => {
  const { encoded } = useParams();
  const navigate = useNavigate();
  const [trade, setTrade] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCta, setShowCta] = useState(false);

  useEffect(() => {
    const loadTrade = () => {
      try {
        setIsLoading(true);
        setError(null);

        // Extract encoded data from URL parameter
        const encodedData = encoded || extractEncodedFromUrl(window.location.href);
        
        if (!encodedData) {
          throw new Error('Invalid share link');
        }

        // Decode trade data
        const decodedTrade = decodeTradeData(encodedData);
        
        if (!decodedTrade) {
          throw new Error('Failed to decode trade data');
        }

        setTrade(decodedTrade);
      } catch (err) {
        console.error('Error loading shared trade:', err);
        setError(err.message || 'Failed to load trade');
      } finally {
        setIsLoading(false);
      }
    };

    loadTrade();
  }, [encoded]);

  // Gently prompt user to start investing in perps after viewing the shared trade
  useEffect(() => {
    if (!trade) return;

    // Avoid spamming the popup on every visit if user has already seen it
    const hasSeenCta = window.localStorage.getItem('perps_share_cta_seen');
    if (hasSeenCta) return;

    const timer = setTimeout(() => {
      setShowCta(true);
      window.localStorage.setItem('perps_share_cta_seen', '1');
    }, 5000); // show after 5 seconds so user can first explore the shared trade

    return () => clearTimeout(timer);
  }, [trade]);

  const handleClose = () => {
    // Navigate to markets page when closing
    navigate('/perps/markets/crypto');
  };

  const handleStartInvesting = () => {
    navigate('/perps/markets/crypto');
  };

  const handleShare = (imageDataUrl) => {
    // Optional: Handle sharing the image
    console.log('Image shared:', imageDataUrl);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md">
        <div className="text-white text-xl">Loading trade...</div>
      </div>
    );
  }

  if (error || !trade) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Share Link</h2>
          <p className="text-gray-600 mb-6">{error || 'This trade link is invalid or expired.'}</p>
          <button
            onClick={() => navigate('/perps/markets/crypto')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Go to Markets
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <TradeResultCard 
        trade={trade} 
        onClose={handleClose}
        onShare={handleShare}
      />

      {showCta && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4">
          <div className="relative w-full max-w-md rounded-2xl bg-gradient-to-b from-[#f7f4ff] via-white to-[#f9f7ff] shadow-[0_18px_45px_rgba(15,23,42,0.35)] border border-white/60 overflow-hidden">
            {/* Accent strip */}
            <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-cyan-400" />

            <button
              onClick={() => setShowCta(false)}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="p-6 sm:p-7">
              <div className="mb-3 inline-flex items-center rounded-full bg-[#F3EAFF] px-3 py-1 text-xs font-semibold text-[#5B1DEE]">
                Powered by Coded Estate Perps
              </div>
              <h2 className="text-2xl font-bold text-[#1F2933] mb-2">
                Turn this shared trade into <span className="text-[#5B1DEE]">your</span> first position
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mb-5">
                You just viewed a perp trade. Jump into the main markets to discover pairs, tweak leverage, and place your own positions in a few clicks.
              </p>

              <div className="w-full mb-3">
                <PurpleButton
                  text="Start investing in perps"
                  onClick={handleStartInvesting}
                  className="w-full flex items-center justify-center text-sm sm:text-base font-semibold"
                />
              </div>

              <button
                onClick={() => setShowCta(false)}
                className="w-full text-center text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Maybe later, keep showing me this trade
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareTradePage;

